import { useMergeRefs } from '@chakra-ui/react';
import { forwardRef } from '@chakra-ui/system';
import { defaultCountries, usePhoneInput } from 'react-international-phone';
import { useMeasure } from 'react-use';

import { InputGroup, type InputGroupProps } from '@frontend/components';

import { PhoneInputProvider } from './usePhoneInputContext';

export type PhoneInputProps = InputGroupProps & {
  defaultCountry: string;
  defaultValue?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isReadOnly?: boolean;
};

export const PhoneInput = forwardRef<PhoneInputProps, 'div'>((props, ref) => {
  const {
    defaultCountry,
    defaultValue,
    value,
    onChange,
    children,
    isDisabled,
    isInvalid,
    isReadOnly,
    ...restProps
  } = props;
  const defaultCountryLowercase = defaultCountry.toLowerCase();
  const defaultCountryIso2 =
    defaultCountries.find(
      (countryData) => countryData[1] === defaultCountryLowercase,
    )?.[1] ?? 'us';
  const phoneInput = usePhoneInput({
    defaultCountry: defaultCountryIso2,
    value: value === undefined ? defaultValue : value ?? '',
    countries: defaultCountries,
    onChange: (data) => {
      onChange?.(data.phone);
    },
  });

  const [measureRef, { width: inputWidth }] = useMeasure();
  const mergedRefs = useMergeRefs(ref, measureRef);

  return (
    <PhoneInputProvider
      value={{
        ...phoneInput,
        inputWidth,
        stateProps: { isDisabled, isInvalid, isReadOnly },
      }}
    >
      <InputGroup ref={mergedRefs} {...restProps}>
        {children}
      </InputGroup>
    </PhoneInputProvider>
  );
});
